import type {
  IFilterOptions,
  IOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IPriorityFilters } from "../types";
import { formatTreatment } from "../utils";
import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { VulnerabilityTreatment } from "gql/graphql";
import { useFiltersStorage } from "storage/filters";
import { getCheckedFilter } from "utils/filters";

const TREATMENTS = [...Object.values(VulnerabilityTreatment)];

const usePriorityFilters = ({
  setTreatment,
}: Readonly<IPriorityFilters>): IUseFilterProps<IVulnRowAttr> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const options: IOptionsProps<IVulnRowAttr>[] = useMemo(
    (): IOptionsProps<IVulnRowAttr>[] => [
      {
        filterOptions: [
          {
            hideSearch: true,
            key: "treatmentStatus",
            label: t("searchFindings.tabVuln.vulnTable.treatment"),
            options: TREATMENTS.map(
              (value): IOptions => ({
                checked: value.toUpperCase() === "UNTREATED",
                label: formatTreatment(value),
                value: value.toUpperCase(),
              }),
            ),
            type: "radioButton",
          },
        ],
        label: t("searchFindings.tabVuln.vulnTable.treatment"),
      },
    ],
    [t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("fix_priority") as string | null;
  }, [getValues]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("fix_priority", value);
    },
    [setValues],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IVulnRowAttr>[]): void => {
      const newFiltersArray = newFilters as IFilterOptions<IVulnRowAttr>[];
      const treatmentValue = getCheckedFilter(
        newFiltersArray,
        "treatmentStatus",
      )?.value;
      setTreatment(treatmentValue ?? "");
    },
    [setTreatment],
  );

  return useFilters({
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options,
    setValues: handleSetValues,
  });
};

export { usePriorityFilters };
